import { ModalAction } from "@/components/common/ModalAction";
import { UserForm } from "../user-form";
import EditIcon from "@mui/icons-material/Edit";
import { AxiosError, type TCommercial, updateUser } from "@/api";
import { phonePrefixChoices } from "@/config/country";
import { useCallback, useMemo } from "react";
import {
  RecordContextProvider,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";

// Phone in the commercial DB is stored concatenated (`+573203338803`).
// The form has separate `phonePrefix` + `phone` inputs, so prefill needs
// to split the stored value on the longest matching prefix. On save the
// two pieces are re-joined into the same concatenated shape.
function splitPhone(stored: string | undefined | null) {
  const value = (stored ?? "").trim();
  if (!value) return { phonePrefix: undefined, phone: "" };
  const prefixes = phonePrefixChoices
    .map((p) => p.id)
    .sort((a, b) => b.length - a.length); // longest first so "+57" wins over "+1"
  for (const prefix of prefixes) {
    if (value.startsWith(prefix)) {
      return { phonePrefix: prefix, phone: value.slice(prefix.length) };
    }
  }
  return { phonePrefix: undefined, phone: value };
}

// Inline-row Edit action for supplier users — opens a modal with the same
// UserForm used for creation, in edit mode (password is optional). Calls
// the new `/api/suppliers/admin/users` endpoint exposed via updateUser().
// Avoids navigating to a standalone edit route (the supplier context would
// be lost — see users.tsx where rowClick is disabled for the same reason).
export const SupplierEditUserButton = () => {
  const record = useRecordContext<TCommercial>();
  const notify = useNotify();
  const refresh = useRefresh();

  const formRecord = useMemo(() => {
    if (!record) return record;
    const { phonePrefix, phone } = splitPhone(record.phone as string);
    return { ...record, phonePrefix, phone };
  }, [record]);

  const onSubmit = useCallback(
    async (values: any, onClose: Function) => {
      if (!record?.id) return;
      // The phone+prefix UI splits the value; combine before sending unless
      // the user kept the field empty (now optional).
      const phone =
        values.phone && values.phonePrefix
          ? values.phonePrefix + values.phone
          : values.phone || undefined;

      const payload: Partial<TCommercial> = {
        ...record,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        roleId: values.roleId,
        phone,
        password: values.password || undefined,
        supplierId: record.supplierId,
      };
      try {
        await updateUser(String(record.id), payload);
        notify("Usuario actualizado", { type: "success" });
        refresh();
        onClose();
      } catch (error: any) {
        let errorMessage: string | undefined;
        if (error instanceof AxiosError) {
          errorMessage = error.response?.data?.message;
        }
        notify(errorMessage || "Error al actualizar el usuario.", {
          type: "warning",
        });
      }
    },
    [record, notify, refresh],
  );

  if (!record) return null;

  return (
    <ModalAction
      buttonMode="icon"
      buttonIcon={<EditIcon fontSize="small" />}
      buttonVariant="text"
      tooltipTitle="Editar usuario"
      dialogTitle="Editar usuario"
      dialogContent={(onClose) => (
        <RecordContextProvider value={formRecord}>
          <UserForm
            onSubmit={(values) => onSubmit(values, onClose)}
            supplierId={record.supplierId!}
            isEdit
          />
        </RecordContextProvider>
      )}
    />
  );
};
